import type { ColumnDirection } from '@hotjar/hotsauce';
import { Table, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { useMemo, useState } from 'react';
import { useNavigation } from '@hotjar/navigation';
import { useTranslation, formatPercentage } from '@hotjar/translations';
import type { AggregatedResult } from '../../../../domain/Surveys/Results/AggregatedResult';

export const SmallHeadCell = styled(Table.HeadCell)`
  width: 1px; /* Set very small size to force cell to auto-size */
`;

export const SmallCell = styled(Table.Cell)`
  width: 1px; /* Set very small size to force cell to auto-size */
`;

export const CenteredCell = styled(Table.Cell)`
  text-align: center;
`;

export const StyledRow = styled(Table.Row)<{ isHighlighted?: boolean }>`
  --background-color: ${(p) =>
    p.isHighlighted === true ? tokens.BRAND_COLORS.noon_100 : tokens.color.white};
  &:hover {
    background-color: var(--background-color);
  }

  background-color: var(--background-color);

  ${(p) =>
    p.isHighlighted === true &&
    `
  td {
    ${tokens.text('link')}
  }
  `};
`;

type StringColumnKey = 'question' | 'answer';
type NumberColumnKey = 'count' | 'percent';

export type ColumnKey = StringColumnKey | NumberColumnKey;

export type RowData = Record<StringColumnKey, string> & Record<NumberColumnKey, number>;

export interface SortState {
  key: ColumnKey;
  direction: ColumnDirection;
}

export interface AnswersTableProps {
  result: AggregatedResult;
  showIndex: boolean;
  isSticky?: boolean;
}

export const toLetter = (index: number): string => {
  return String.fromCharCode(65 + index);
};

export const toRowData = (result: AggregatedResult): RowData[] => {
  return Object.keys(result.answers).map((key, index) => {
    return {
      question: toLetter(index),
      answer: key,
      count: result.answers[key],
      percent: result.pc_answers[key],
    };
  });
};

export const compareNumber = (a: number, b: number, direction: ColumnDirection): number => {
  return (a - b) * (direction === 'asc' ? 1 : -1);
};

export const compareString = (a: string, b: string, direction: ColumnDirection): number => {
  return a.localeCompare(b, undefined, { numeric: true }) * (direction === 'asc' ? 1 : -1);
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, func-params-args/func-params
export const sortWithFallback = <T extends unknown>(
  valueA: T,
  valueB: T,
  sortDirection: ColumnDirection,
  sort: (a: T, b: T, direction: ColumnDirection) => number,
  fallbackSort: (a: T, b: T, direction: ColumnDirection) => number,
): number => {
  const result = sort(valueA, valueB, sortDirection);

  return result === 0 ? fallbackSort(valueA, valueB, sortDirection) : result;
};

const getCompare = (key: ColumnKey, sortDirection: ColumnDirection) => {
  // Fallback to question sort to ensure stable ordering
  const questionSort = (a: RowData, b: RowData, direction: ColumnDirection) =>
    compareString(a.question, b.question, direction);

  switch (key) {
    case 'question':
      return (a: RowData, b: RowData) => questionSort(a, b, sortDirection);
    case 'answer':
      return (rowA: RowData, rowB: RowData) =>
        sortWithFallback(
          rowA,
          rowB,
          sortDirection,
          (a, b, direction) => compareString(a[key], b[key], direction),
          questionSort,
        );
    case 'count':
    case 'percent':
      return (rowA: RowData, rowB: RowData) =>
        sortWithFallback(
          rowA,
          rowB,
          sortDirection,
          (a, b, direction) => compareNumber(a[key], b[key], direction),
          questionSort,
        );
    default:
      throw new Error('Unexpected key');
  }
};

export const useAnswersTableRowData = (
  result: AggregatedResult,
  defaultSortColumn: ColumnKey,
): [
  RowData[],
  (columnKey: ColumnKey) => ColumnDirection,
  (columnKey: ColumnKey) => (newDirection: ColumnDirection) => void,
] => {
  const [sortState, setSortState] = useState<SortState>({
    key: defaultSortColumn,
    direction: 'asc',
  });

  const rowData = useMemo(
    () => toRowData(result).sort(getCompare(sortState.key, sortState.direction)),
    [result, sortState],
  );

  const getSortDirection = (columnKey: ColumnKey): ColumnDirection =>
    sortState.key === columnKey ? sortState.direction : 'none';

  const getOnSort = (columnKey: ColumnKey) => {
    return (newDirection: ColumnDirection) =>
      setSortState({ key: columnKey, direction: newDirection });
  };

  return [rowData, getSortDirection, getOnSort];
};

export const useHighestValue = (result: AggregatedResult): number => {
  return useMemo(() => Math.max(...Object.values(result.answers)), [result]);
};

export const AnswersTable = (props: AnswersTableProps): JSX.Element => {
  const { result, showIndex, isSticky = true } = props;

  const [rowData, getSortDirection, getOnSort] = useAnswersTableRowData(result, 'question');

  const highestValue = useHighestValue(result);

  const { topBarHeight: topNavHeight } = useNavigation();

  const { t, i18n } = useTranslation('feedback');

  const caption = t('surveys.answer-table.caption', { count: result.total });

  return (
    <Table
      caption={caption}
      tableName={t('surveys.answer-table.name')}
      withStickyLastColumn={false}
    >
      <Table.Head>
        <Table.HeadRow isSticky={isSticky} stickyOffset={topNavHeight}>
          {showIndex && (
            <SmallHeadCell
              isSortable
              title={t('surveys.answer-table.question')}
              sortDirection={getSortDirection('question')}
              onSort={getOnSort('question')}
            >
              {t('common.number-sign')}
            </SmallHeadCell>
          )}
          <Table.HeadCell
            isSortable
            title={t('surveys.answer-table.answer')}
            sortDirection={getSortDirection('answer')}
            onSort={getOnSort('answer')}
          >
            {t('surveys.answer-table.answer')}
          </Table.HeadCell>
          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.count')}
            sortDirection={getSortDirection('count')}
            onSort={getOnSort('count')}
          >
            {t('surveys.answer-table.count')}
          </SmallHeadCell>

          <SmallHeadCell
            isSortable
            title={t('surveys.answer-table.percentage')}
            sortDirection={getSortDirection('percent')}
            onSort={getOnSort('percent')}
          >
            {t('common.percent-sign')}
          </SmallHeadCell>
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        <>
          {rowData.map((row) => {
            return (
              <StyledRow
                key={row.question}
                isHighlighted={highestValue === row.count}
                aria-label={
                  highestValue === row.count ? t('surveys.answer-table.most-frequent') : undefined
                }
              >
                {showIndex && <SmallCell>{row.question}</SmallCell>}
                <Table.Cell>{row.answer}</Table.Cell>
                <SmallCell>{row.count.toLocaleString(i18n.language)}</SmallCell>
                <SmallCell>{formatPercentage(row.percent, i18n)}</SmallCell>
              </StyledRow>
            );
          })}
        </>
      </Table.Body>
      <Table.Foot>
        <StyledRow>
          <CenteredCell colSpan={showIndex ? 4 : 3}>{caption}</CenteredCell>
        </StyledRow>
      </Table.Foot>
    </Table>
  );
};
